/**
 * Cashier POS Operations Guide - Mobile-Friendly Guide for Cashiers
 * Complete guide for POS operations, till management, and shift procedures
 */
import { useState } from 'react';
import { 
  Printer, DollarSign, CreditCard, ShoppingCart, User, CheckCircle, 
  AlertTriangle, ChevronRight, Shield, LogIn, LogOut, Settings,
  Calculator, Receipt, Coffee, RefreshCw, XCircle, Clock,
  Smartphone, Banknote, Wallet, FileText, CheckSquare, AlertCircle
} from 'lucide-react';

export default function CashierGuidePage() {
  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .page-break { page-break-after: always; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .guide-section { break-inside: avoid; }
        }
        @page {
          size: A4;
          margin: 15mm;
        }
      `}</style>

      {/* Print Header - Only visible on screen */}
      <div className="no-print bg-amber-700 text-white py-4 px-6 sticky top-0 z-50 shadow-lg">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <DollarSign className="w-8 h-8" />
            <div>
              <h1 className="text-xl font-bold">Bulaccino Cafe - Cashier Guide</h1>
              <p className="text-amber-100 text-sm">POS Operations &amp; Till Management</p>
            </div>
          </div>
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-6 py-2 bg-white text-amber-700 rounded-lg font-medium hover:bg-amber-50 transition-colors"
            data-testid="print-cashier-guide-btn"
          >
            <Printer className="w-5 h-5" />
            Print / Download PDF
          </button>
        </div>
      </div>

      {/* Printable Content */}
      <div className="max-w-4xl mx-auto p-6 bg-white text-gray-900">
        {/* Cover Page */}
        <div className="text-center py-12 border-b-4 border-amber-600 mb-8">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center">
              <DollarSign className="w-10 h-10 text-amber-600" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Cashier Operations Guide</h1>
          <p className="text-xl text-gray-600 mb-2">Bulaccino Cafe - Nadi, Fiji</p>
          <p className="text-gray-500">POS System | Till Management | Shift Procedures</p>
          <div className="mt-8 inline-block bg-amber-50 border border-amber-200 rounded-lg px-6 py-3">
            <p className="text-amber-800 font-medium">POS URL: bulaccinocafe.com/pos</p>
          </div>
        </div>

        {/* Table of Contents */}
        <div className="mb-8 p-6 bg-gray-50 rounded-xl">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Table of Contents</h2>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-gray-700">
              <ChevronRight className="w-4 h-4 text-amber-600" />
              <span>1. Starting Your Shift</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <ChevronRight className="w-4 h-4 text-amber-600" />
              <span>2. Logging Into the POS</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <ChevronRight className="w-4 h-4 text-amber-600" />
              <span>3. Taking Orders</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <ChevronRight className="w-4 h-4 text-amber-600" />
              <span>4. Processing Payments</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <ChevronRight className="w-4 h-4 text-amber-600" />
              <span>5. Cash Handling</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <ChevronRight className="w-4 h-4 text-amber-600" />
              <span>6. Applying Discounts</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <ChevronRight className="w-4 h-4 text-amber-600" />
              <span>7. Voiding Items</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <ChevronRight className="w-4 h-4 text-amber-600" />
              <span>8. Coffee Pass / Bula Club</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <ChevronRight className="w-4 h-4 text-amber-600" />
              <span>9. End of Shift Procedures</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <ChevronRight className="w-4 h-4 text-amber-600" />
              <span>10. Troubleshooting</span>
            </div>
          </div>
        </div>

        {/* Section 1: Starting Your Shift */}
        <div className="guide-section mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">1. Starting Your Shift</h2>
          </div>
          
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-4">
            <h3 className="font-bold text-amber-800 mb-3">Before You Start:</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-gray-900">Clock In First</p>
                  <p className="text-gray-600 text-sm">Go to bulaccinocafe.com/time-clock and clock in before starting work</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-gray-900">Check the Till Float</p>
                  <p className="text-gray-600 text-sm">Count the cash drawer and verify it matches the opening float amount</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-gray-900">Check Receipt Printer</p>
                  <p className="text-gray-600 text-sm">Make sure the printer has paper and is turned on</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-gray-900">Check EFTPOS Terminal</p>
                  <p className="text-gray-600 text-sm">Ensure card machine is on and connected</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section 2: Logging Into POS */}
        <div className="guide-section mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <LogIn className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">2. Logging Into the POS</h2>
          </div>

          <div className="space-y-4">
            <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
              <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">1</div>
              <div>
                <p className="font-medium text-gray-900">Open the POS</p>
                <p className="text-gray-600">Go to: <span className="font-mono bg-gray-200 px-2 py-0.5 rounded">bulaccinocafe.com/pos</span></p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
              <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">2</div>
              <div>
                <p className="font-medium text-gray-900">Enter Your PIN</p>
                <p className="text-gray-600">Type your 6-digit PIN on the number pad</p>
                <p className="text-gray-500 text-sm mt-1">Your manager will give you your PIN</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
              <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">3</div>
              <div>
                <p className="font-medium text-gray-900">Select Customer (Optional)</p>
                <p className="text-gray-600">Search for a customer or tap "Skip" for walk-in guests</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 bg-green-50 rounded-xl border border-green-200">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
              <div>
                <p className="font-medium text-green-800">You're Logged In!</p>
                <p className="text-green-700">You'll see the menu categories on the left and the order cart on the right</p>
              </div>
            </div>
          </div>
        </div>

        <div className="page-break"></div>

        {/* Section 3: Taking Orders */}
        <div className="guide-section mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <ShoppingCart className="w-5 h-5 text-purple-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">3. Taking Orders</h2>
          </div>

          <div className="space-y-4">
            <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
              <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">1</div>
              <div>
                <p className="font-medium text-gray-900">Select Category</p>
                <p className="text-gray-600">Tap a category: Coffee, Breakfast, Lunch, Drinks, etc.</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
              <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">2</div>
              <div>
                <p className="font-medium text-gray-900">Tap Item to Add</p>
                <p className="text-gray-600">Tap any menu item to add it to the order</p>
                <p className="text-gray-500 text-sm mt-1">The item appears in the cart on the right</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
              <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">3</div>
              <div>
                <p className="font-medium text-gray-900">Customize Item (if needed)</p>
                <p className="text-gray-600">Tap "Tap to customize" on the item card to:</p>
                <ul className="list-disc list-inside text-gray-500 text-sm mt-1 ml-2">
                  <li>Change size (Regular/Large)</li>
                  <li>Add extras (extra shot, milk alternatives)</li>
                  <li>Remove ingredients</li>
                  <li>Add special instructions</li>
                </ul>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
              <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">4</div>
              <div>
                <p className="font-medium text-gray-900">Adjust Quantity</p>
                <p className="text-gray-600">Use the + and - buttons in the cart to change quantity</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
              <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">5</div>
              <div>
                <p className="font-medium text-gray-900">Remove Item</p>
                <p className="text-gray-600">Tap the trash icon next to an item to remove it</p>
              </div>
            </div>
          </div>
        </div>

        {/* Section 4: Processing Payments */}
        <div className="guide-section mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">4. Processing Payments</h2>
          </div>

          <div className="space-y-4">
            <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
              <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">1</div>
              <div>
                <p className="font-medium text-gray-900">Tap "Pay" Button</p>
                <p className="text-gray-600">The green Pay button is at the bottom of the cart</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
              <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">2</div>
              <div>
                <p className="font-medium text-gray-900">Select Payment Method</p>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div className="flex items-center gap-2 p-2 bg-white rounded border">
                    <Banknote className="w-4 h-4 text-green-600" />
                    <span className="text-sm">Cash</span>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-white rounded border">
                    <CreditCard className="w-4 h-4 text-blue-600" />
                    <span className="text-sm">Card/EFTPOS</span>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-white rounded border">
                    <Smartphone className="w-4 h-4 text-purple-600" />
                    <span className="text-sm">M-PAiSA</span>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-white rounded border">
                    <Coffee className="w-4 h-4 text-amber-600" />
                    <span className="text-sm">Coffee Pass</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Cash Payment */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <h4 className="font-bold text-green-800 mb-2 flex items-center gap-2">
                <Banknote className="w-5 h-5" />
                For Cash Payments:
              </h4>
              <ol className="list-decimal list-inside text-green-700 space-y-1 text-sm">
                <li>Enter the amount the customer gave you</li>
                <li>The system shows the change to give back</li>
                <li>Give change to customer</li>
                <li>Tap "Confirm" to complete the sale</li>
              </ol>
            </div>

            {/* Card Payment */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <h4 className="font-bold text-blue-800 mb-2 flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                For Card/EFTPOS Payments:
              </h4>
              <ol className="list-decimal list-inside text-blue-700 space-y-1 text-sm">
                <li>Select "Card" as payment method</li>
                <li>Customer taps/inserts card on EFTPOS terminal</li>
                <li>Wait for "Approved" on terminal</li>
                <li>Tap "Confirm" in POS to complete</li>
              </ol>
            </div>

            <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
              <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">3</div>
              <div>
                <p className="font-medium text-gray-900">Receipt Prints Automatically</p>
                <p className="text-gray-600">The fiscal receipt prints after payment is confirmed</p>
              </div>
            </div>
          </div>
        </div>

        <div className="page-break"></div>

        {/* Section 5: Cash Handling */}
        <div className="guide-section mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Wallet className="w-5 h-5 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">5. Cash Handling</h2>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-4">
            <h3 className="font-bold text-amber-800 mb-3 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Cash Handling Rules:
            </h3>
            <ul className="space-y-2 text-amber-700">
              <li className="flex items-start gap-2">
                <CheckSquare className="w-4 h-4 mt-1 flex-shrink-0" />
                <span>Always count cash in front of the customer</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckSquare className="w-4 h-4 mt-1 flex-shrink-0" />
                <span>Keep large notes on top of till until change given</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckSquare className="w-4 h-4 mt-1 flex-shrink-0" />
                <span>Count change back to the customer</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckSquare className="w-4 h-4 mt-1 flex-shrink-0" />
                <span>Keep till closed when not in use</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckSquare className="w-4 h-4 mt-1 flex-shrink-0" />
                <span>Never leave till unattended</span>
              </li>
            </ul>
          </div>

          <div className="bg-gray-50 rounded-xl p-4">
            <h4 className="font-bold text-gray-900 mb-2">Opening Float:</h4>
            <p className="text-gray-600">The till should start with $200 float each day</p>
            <p className="text-gray-500 text-sm mt-1">Count and verify before starting your shift</p>
          </div>
        </div>

        {/* Section 6: Applying Discounts */}
        <div className="guide-section mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
              <Receipt className="w-5 h-5 text-pink-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">6. Applying Discounts</h2>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
            <div className="flex items-center gap-2 text-red-800">
              <Shield className="w-5 h-5" />
              <span className="font-bold">Manager/Owner Approval Required</span>
            </div>
            <p className="text-red-700 text-sm mt-1">Cashiers cannot apply discounts. Ask a manager for help.</p>
          </div>

          <div className="space-y-4">
            <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
              <div className="w-8 h-8 bg-pink-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">1</div>
              <div>
                <p className="font-medium text-gray-900">Call Manager</p>
                <p className="text-gray-600">Ask manager to apply discount with their PIN</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
              <div className="w-8 h-8 bg-pink-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">2</div>
              <div>
                <p className="font-medium text-gray-900">Manager Applies Discount</p>
                <p className="text-gray-600">They tap "Discount" and enter the percentage or amount</p>
              </div>
            </div>
          </div>
        </div>

        {/* Section 7: Voiding Items */}
        <div className="guide-section mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <XCircle className="w-5 h-5 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">7. Voiding Items</h2>
          </div>

          <div className="space-y-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <h4 className="font-bold text-gray-900 mb-2">To Remove Item BEFORE Payment:</h4>
              <p className="text-gray-600">Tap the trash icon next to the item in the cart</p>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <h4 className="font-bold text-red-800 mb-2">To Void AFTER Payment:</h4>
              <p className="text-red-700">Only managers/owners can void completed orders.</p>
              <p className="text-red-600 text-sm mt-1">Voids can only be done within 5 minutes of the sale.</p>
            </div>
          </div>
        </div>

        <div className="page-break"></div>

        {/* Section 8: Coffee Pass */}
        <div className="guide-section mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Coffee className="w-5 h-5 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">8. Coffee Pass / Bula Club</h2>
          </div>

          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <h4 className="font-bold text-amber-800 mb-2">What is Coffee Pass?</h4>
              <p className="text-amber-700">Customers pay $50 upfront for 30 coffees over 30 days</p>
            </div>

            <div className="bg-gray-50 rounded-xl p-4">
              <h4 className="font-bold text-gray-900 mb-2">Customer Has Coffee Pass:</h4>
              <ol className="list-decimal list-inside text-gray-600 space-y-1">
                <li>Search for customer by name or phone</li>
                <li>Add their coffee to the cart</li>
                <li>Select "Coffee Pass" as payment method</li>
                <li>The system deducts from their remaining coffees</li>
              </ol>
            </div>

            <div className="bg-gray-50 rounded-xl p-4">
              <h4 className="font-bold text-gray-900 mb-2">Selling a New Coffee Pass:</h4>
              <ol className="list-decimal list-inside text-gray-600 space-y-1">
                <li>Go to Quick Actions menu</li>
                <li>Tap "Sell Coffee Pass"</li>
                <li>Search or create customer</li>
                <li>Process $50 payment</li>
              </ol>
            </div>
          </div>
        </div>

        {/* Section 9: End of Shift */}
        <div className="guide-section mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <LogOut className="w-5 h-5 text-indigo-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">9. End of Shift Procedures</h2>
          </div>

          <div className="space-y-4">
            <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
              <div className="w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">1</div>
              <div>
                <p className="font-medium text-gray-900">Complete All Pending Orders</p>
                <p className="text-gray-600">Make sure all orders are paid and receipts printed</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
              <div className="w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">2</div>
              <div>
                <p className="font-medium text-gray-900">Count the Till</p>
                <p className="text-gray-600">Count all cash in the drawer</p>
                <div className="mt-2 p-3 bg-white rounded border text-sm">
                  <p><span className="font-medium">Expected:</span> Opening Float + Cash Sales</p>
                  <p className="text-gray-500">The POS shows your expected cash total</p>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
              <div className="w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">3</div>
              <div>
                <p className="font-medium text-gray-900">Report Cash Count to Manager</p>
                <p className="text-gray-600">Tell manager if over or under</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
              <div className="w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">4</div>
              <div>
                <p className="font-medium text-gray-900">Log Out of POS</p>
                <p className="text-gray-600">Tap your name at the top right and select "Log Out"</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
              <div className="w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">5</div>
              <div>
                <p className="font-medium text-gray-900">Clock Out</p>
                <p className="text-gray-600">Go to bulaccinocafe.com/time-clock and clock out</p>
              </div>
            </div>
          </div>
        </div>

        {/* Section 10: Troubleshooting */}
        <div className="guide-section mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">10. Troubleshooting</h2>
          </div>

          <div className="space-y-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <h4 className="font-bold text-gray-900 mb-2">Receipt Not Printing?</h4>
              <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                <li>Check printer has paper</li>
                <li>Check printer is turned on</li>
                <li>Check cable connection</li>
                <li>Ask manager to restart printer</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-4">
              <h4 className="font-bold text-gray-900 mb-2">Card Payment Declined?</h4>
              <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                <li>Ask customer to try again</li>
                <li>Try a different card</li>
                <li>Accept cash instead</li>
                <li>Check EFTPOS terminal connection</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-4">
              <h4 className="font-bold text-gray-900 mb-2">POS Screen Frozen?</h4>
              <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                <li>Refresh the browser (pull down on mobile)</li>
                <li>Close and reopen the browser</li>
                <li>Check WiFi connection</li>
                <li>Call manager for help</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-4">
              <h4 className="font-bold text-gray-900 mb-2">Till Doesn't Balance?</h4>
              <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                <li>Recount the cash carefully</li>
                <li>Check for notes stuck together</li>
                <li>Report to manager immediately</li>
                <li>Manager will investigate</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Quick Reference Card */}
        <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-6 mt-8">
          <h3 className="text-xl font-bold text-amber-800 mb-4 text-center">Quick Reference</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="font-bold text-gray-900">POS URL:</p>
              <p className="text-gray-600">bulaccinocafe.com/pos</p>
            </div>
            <div>
              <p className="font-bold text-gray-900">Time Clock:</p>
              <p className="text-gray-600">bulaccinocafe.com/time-clock</p>
            </div>
            <div>
              <p className="font-bold text-gray-900">Opening Float:</p>
              <p className="text-gray-600">$200</p>
            </div>
            <div>
              <p className="font-bold text-gray-900">Need Help?</p>
              <p className="text-gray-600">Ask your manager</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-6 border-t border-gray-200 text-center text-gray-500 text-sm">
          <p>Bulaccino Cafe - Cashier Operations Guide</p>
          <p>bulaccinocafe.com</p>
        </div>
      </div>
    </>
  );
}
